import { z } from "zod";
import { idSchema } from "./ids";

/**
 * The one catalog of value types (decision `2026-11-02-abacor-builds-a-playbook-from-one-concept-map`,
 * item 9). A table column, a slot and an output value of the same type validate with the same
 * schema, so a value read from one can be written to another. Columns, slots and output values
 * each use a view of this list (`COLUMN_TYPES` in `data-tables`, `SLOT_TYPES` in `processes`,
 * `OUTPUT_VALUE_KINDS` here). Contacts and sites join when they have IDs.
 *
 * Spellings are camelCase, as slots already store them. Columns were stored with two other
 * spellings before the catalog (`yes_no`, `connection`); they keep those spellings, and
 * `valueTypeOf` maps them on read, so no stored table version, pin or release changes.
 */
export const VALUE_TYPES = [
  "text",
  "number",
  "count",
  "amount",
  "date",
  "duration",
  "timeOfDay",
  "yesNo",
  "choice",
  "member",
  "row",
  "login",
  "email",
] as const;
export type ValueType = (typeof VALUE_TYPES)[number];

/** The types a person may move within a range: a slot's range compares two of them. */
export const RANGED_VALUE_TYPES = ["duration", "count", "amount", "timeOfDay"] as const;
export type RangedValueType = (typeof RANGED_VALUE_TYPES)[number];

/** The types a slot holds: a member, or a ranged value. */
export const SLOT_VALUE_TYPES = ["member", ...RANGED_VALUE_TYPES] as const;

/** The kinds a `value` output may declare (plan `2026-11-01-typed-slots-and-undo-stack`, Part 3). */
export const OUTPUT_VALUE_KINDS = ["text", "number", "amount", "date", "yesNo", "choice"] as const;
export type OutputValueKind = (typeof OUTPUT_VALUE_KINDS)[number];

/** A duration's units, in two families: calendar time, and business days. */
export const DURATION_UNITS = ["minutes", "hours", "businessDays", "days", "weeks"] as const;
export type DurationUnit = (typeof DURATION_UNITS)[number];

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const isCalendarDate = (value: string) => {
  if (!DATE.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

export const textValueSchema = z
  .string()
  .trim()
  .min(1)
  .max(2000)
  .refine((value) => !value.includes("\0"));
export const numberValueSchema = z.number().finite();
export const countValueSchema = z.int().min(0).max(1_000_000);
/** Money in minor units (cents), with its ISO 4217 currency. */
export const amountValueSchema = z
  .object({
    minor: z.int().min(0).max(Number.MAX_SAFE_INTEGER),
    currency: z.string().regex(/^[A-Z]{3}$/),
  })
  .strict();
/** A calendar date, `YYYY-MM-DD`. */
export const dateValueSchema = z.string().refine(isCalendarDate);
export const durationValueSchema = z
  .object({ amount: z.int().min(1).max(9999), unit: z.enum(DURATION_UNITS) })
  .strict();
/** `HH:MM`, always in the workspace's time zone. */
export const timeOfDayValueSchema = z
  .object({ time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/) })
  .strict();
export const yesNoValueSchema = z.boolean();
/**
 * A workspace member, by user ID (decision `2026-11-01-step-values-members-and-waiting-for-replies`).
 * Only people who have joined; an invitation is not a member.
 */
export const memberRefSchema = z.object({ userId: idSchema("user") }).strict();
/** A row of another table. */
export const rowValueSchema = idSchema("dataTableRow");
/** A login: one of the workspace's connections. */
export const loginValueSchema = idSchema("connection");
export const emailValueSchema = z.email().max(254);

export type AmountValue = z.infer<typeof amountValueSchema>;
export type DurationValue = z.infer<typeof durationValueSchema>;
export type TimeOfDayValue = z.infer<typeof timeOfDayValueSchema>;
export type MemberRef = z.infer<typeof memberRefSchema>;

/** Any value of the catalog, whatever its type. */
export type Value =
  | string
  | number
  | boolean
  | AmountValue
  | DurationValue
  | TimeOfDayValue
  | MemberRef;

/** Any value of the catalog, checked for its shape only: what a stored copy of typed values holds. */
export const anyValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  amountValueSchema,
  durationValueSchema,
  timeOfDayValueSchema,
  memberRefSchema,
]);

/** What a type needs beside its name: a choice's options. */
export type ValueDetail = { options?: readonly string[] | undefined };

/** The schema of one value of a type. A choice needs its options. */
export function valueSchema(type: ValueType, detail: ValueDetail = {}): z.ZodType<Value> {
  switch (type) {
    case "text":
      return textValueSchema;
    case "number":
      return numberValueSchema;
    case "count":
      return countValueSchema;
    case "amount":
      return amountValueSchema;
    case "date":
      return dateValueSchema;
    case "duration":
      return durationValueSchema;
    case "timeOfDay":
      return timeOfDayValueSchema;
    case "yesNo":
      return yesNoValueSchema;
    case "choice": {
      const options = detail.options;
      if (!options || options.length === 0) throw new RangeError("A choice needs its options.");
      return z.enum(options as [string, ...string[]]);
    }
    case "member":
      return memberRefSchema;
    case "row":
      return rowValueSchema;
    case "login":
      return loginValueSchema;
    case "email":
      return emailValueSchema;
  }
}

/** The spellings columns were stored under before the catalog, kept so stored hashes stand. */
const COLUMN_SPELLINGS = { yesNo: "yes_no", login: "connection" } as const;
type ColumnSpellings = typeof COLUMN_SPELLINGS;

/** A value type as a column spells it. */
export type ColumnSpelling<Type> = Type extends keyof ColumnSpellings
  ? ColumnSpellings[Type]
  : Type;

/** A value type as a column stores it. */
export const columnTypeOf = <Type extends ValueType>(type: Type): ColumnSpelling<Type> =>
  ((COLUMN_SPELLINGS as Record<string, string>)[type] ?? type) as ColumnSpelling<Type>;

/** The column types: every value type, in the column spelling. */
export const COLUMN_VALUE_TYPES = VALUE_TYPES.map(columnTypeOf) as unknown as {
  readonly [Index in keyof typeof VALUE_TYPES]: ColumnSpelling<(typeof VALUE_TYPES)[Index]>;
};
export type ColumnValueType = (typeof COLUMN_VALUE_TYPES)[number];

const FROM_COLUMN = Object.fromEntries(
  Object.entries(COLUMN_SPELLINGS).map(([type, spelling]) => [spelling, type]),
) as Record<string, ValueType>;

/** A column's type read as a value type: `yes_no` is `yesNo`, `connection` is `login`. */
export const valueTypeOf = (type: ColumnValueType): ValueType =>
  FROM_COLUMN[type] ?? (type as ValueType);
